/** Cartographie du dirigeant : les autres sociétés du ou des dirigeants.
 *
 *  Un dirigeant multi-mandats transforme un prospect isolé en portefeuille :
 *  le même interlocuteur, plusieurs sociétés à ouvrir. Le graphe distingue ce
 *  que le CRM connaît déjà (cliquable, avec son statut) de ce que seule
 *  l'API publique révèle — un clic crée alors la fiche depuis le registre
 *  public et lie les deux sociétés.
 *
 *  Le placement est un petit modèle de forces (répulsion + ressorts) calculé
 *  une fois à la réception des données : moins de 100 nœuds, aucune
 *  dépendance de graphe à embarquer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/crmApi";
import { toast } from "sonner";
import { StatusBadge, ScoreBadge } from "@/crm/ui";
import {
  Network, RefreshCw, ZoomIn, ZoomOut, LocateFixed, ExternalLink, Plus,
  Building2, UserRound, Loader2, X, Info, Link2 as LinkIcon,
} from "lucide-react";

const W = 720, H = 460;

/** Force-directed simplifié : répulsion entre nœuds, ressorts sur les liens,
 *  rappel vers le centre. Déterministe (positions initiales en cercle) pour
 *  qu'un même réseau se redessine toujours pareil. */
function layout(nodes, links, iterations = 320) {
  const n = nodes.length;
  if (!n) return {};
  const pos = {};
  nodes.forEach((nd, i) => {
    if (nd.is_root) { pos[nd.id] = { x: W / 2, y: H / 2 }; return; }
    const a = (2 * Math.PI * i) / n;
    pos[nd.id] = { x: W / 2 + Math.cos(a) * 170, y: H / 2 + Math.sin(a) * 130 };
  });
  const deg = {};
  links.forEach((l) => { deg[l.source] = (deg[l.source] || 0) + 1; deg[l.target] = (deg[l.target] || 0) + 1; });

  const k = Math.min(120, Math.max(60, 900 / Math.sqrt(n)));
  for (let it = 0; it < iterations; it++) {
    const cool = 1 - it / iterations;
    const disp = {};
    nodes.forEach((nd) => { disp[nd.id] = { x: 0, y: 0 }; });

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = pos[nodes[i].id], b = pos[nodes[j].id];
        let dx = a.x - b.x, dy = a.y - b.y;
        let d2 = dx * dx + dy * dy;
        if (d2 < 1) { dx = (i - j) || 1; dy = 1; d2 = 2; }
        const f = (k * k) / d2;
        const d = Math.sqrt(d2);
        disp[nodes[i].id].x += (dx / d) * f; disp[nodes[i].id].y += (dy / d) * f;
        disp[nodes[j].id].x -= (dx / d) * f; disp[nodes[j].id].y -= (dy / d) * f;
      }
    }
    links.forEach((l) => {
      const a = pos[l.source], b = pos[l.target];
      if (!a || !b) return;
      const dx = a.x - b.x, dy = a.y - b.y;
      const d = Math.max(1, Math.hypot(dx, dy));
      const f = (d * d) / k / 2;
      disp[l.source].x -= (dx / d) * f; disp[l.source].y -= (dy / d) * f;
      disp[l.target].x += (dx / d) * f; disp[l.target].y += (dy / d) * f;
    });

    nodes.forEach((nd) => {
      const p = pos[nd.id], dsp = disp[nd.id];
      // Rappel central pondéré par le degré : les nœuds très connectés
      // (le dirigeant) migrent naturellement au milieu.
      dsp.x += (W / 2 - p.x) * 0.02 * (1 + (deg[nd.id] || 0) * 0.15);
      dsp.y += (H / 2 - p.y) * 0.02 * (1 + (deg[nd.id] || 0) * 0.15);
      const d = Math.max(0.01, Math.hypot(dsp.x, dsp.y));
      const step = Math.min(d, 22 * cool);
      p.x = Math.min(W - 40, Math.max(40, p.x + (dsp.x / d) * step));
      p.y = Math.min(H - 30, Math.max(30, p.y + (dsp.y / d) * step));
    });
  }
  return pos;
}

const nodeRadius = (n) => (n.is_root ? 22 : n.kind === "person" ? 16 : 12);

function nodeFill(n) {
  if (n.is_root) return "#334155";               // ardoise : la fiche courante
  if (n.kind === "person") return "#10b981";     // vert : le dirigeant
  if (!n.in_crm) return "#94a3b8";               // gris : hors CRM
  return "#2563eb";                              // bleu : société déjà en base
}

export default function DirigeantNetwork({ company, onChanged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [external, setExternal] = useState(true);
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [adding, setAdding] = useState(null);
  const drag = useRef(null);

  const load = useCallback(async (ext) => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/companies/${company.id}/reseau`, {
        params: { external: ext ? "true" : "false" },
      });
      setData(res);
      setSelected(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Cartographie indisponible"); }
    finally { setLoading(false); }
  }, [company.id]);

  useEffect(() => { load(external); }, [load, external]);

  const positions = useMemo(
    () => (data ? layout(data.nodes, data.links) : {}),
    [data]);

  const reset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const onWheel = (e) => {
    e.preventDefault();
    setZoom((z) => Math.min(2.5, Math.max(0.5, z - e.deltaY * 0.001)));
  };

  // Le dirigeant à l'origine du lien : il qualifie la liaison enregistrée
  // ensuite sur les deux fiches (« Dirigeant commun : … »).
  const lienDirigeant = useCallback((n) => {
    const l = (data?.links || []).find(
      (x) => x.target === n.id && String(x.source).startsWith("person:"));
    if (!l) return null;
    const p = (data?.nodes || []).find((x) => x.id === l.source);
    return { nom: p?.label || null, qualite: l.label || null };
  }, [data]);

  // Ajout au CRM d'une société repérée hors base : le back va chercher la
  // fiche officielle au registre public à partir du SIREN (siège, NAF,
  // dirigeants, effectif, labels, finances, scoring) — le graphe n'affiche
  // qu'une poignée de champs, insuffisants pour une fiche exploitable — puis
  // enregistre la liaison sur les deux sociétés.
  const addToCrm = async (n) => {
    if (!n.siren) {
      toast.error("SIREN inconnu — impossible de récupérer la fiche au registre public");
      return;
    }
    setAdding(n.id);
    try {
      const origine = lienDirigeant(n);
      const { data: res } = await api.post(`/companies/${company.id}/reseau/liaison`, {
        siren: n.siren, nom: n.label,
        dirigeant: origine?.nom || null, qualite: origine?.qualite || null,
      });
      toast.success(
        res.created ? "Fiche créée depuis le registre public, sociétés liées"
                    : res.refreshed ? "Société déjà au CRM — fiche mise à jour et liée"
                                    : "Société déjà au CRM — liaison établie",
        {
          description: [res.company?.nom, res.dirigeants ? `${res.dirigeants} dirigeant(s)` : null]
            .filter(Boolean).join(" · "),
          action: res.visible
            ? { label: "Ouvrir", onClick: () => navigate(`/entreprises/${res.company.id}`) }
            : undefined,
        });
      onChanged?.();
      load(external);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Création impossible"); }
    finally { setAdding(null); }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-slate-400 text-sm">
        <Loader2 size={16} className="animate-spin" /> Recherche des sociétés liées…
      </div>
    );
  }
  if (!data) return null;

  if (!data.dirigeants.length && !data.stats?.societes_liees) {
    return (
      <div className="text-center py-10" data-testid="reseau-empty">
        <Network size={28} className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-400 mt-2">{data.message || "Aucun dirigeant connu sur cette fiche"}</p>
      </div>
    );
  }

  const st = data.stats;

  return (
    <div data-testid="reseau-panel">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Stat label="Dirigeants" value={st.dirigeants} />
        <Stat label="Sociétés liées" value={st.societes_liees} accent="indigo" />
        <Stat label="Déjà au CRM" value={st.dans_crm} accent="blue" />
        <Stat label="Hors CRM" value={st.hors_crm} accent="slate" />
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer"
            title="Interroger l'API publique en plus des fiches du CRM">
            <input type="checkbox" checked={external} onChange={(e) => setExternal(e.target.checked)} />
            Registre public
          </label>
          <button data-testid="reseau-refresh" onClick={() => load(external)} disabled={loading}
            className="p-1.5 rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-50 disabled:opacity-40" title="Actualiser">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {st.societes_liees === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center">
          <Info size={22} className="mx-auto text-slate-300" />
          <p className="text-sm text-slate-500 mt-2">
            Aucun autre mandat trouvé pour {data.dirigeants.map((d) => d.nom).join(", ")}.
          </p>
          {!external && (
            <button onClick={() => setExternal(true)} className="mt-2 text-xs text-indigo-600 hover:underline">
              Chercher aussi dans le registre public
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="relative rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[460px] touch-none select-none"
              onWheel={onWheel}
              onMouseDown={(e) => { drag.current = { sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y }; }}
              onMouseMove={(e) => {
                if (!drag.current) return;
                setPan({ x: drag.current.px + (e.clientX - drag.current.sx), y: drag.current.py + (e.clientY - drag.current.sy) });
              }}
              onMouseUp={() => { drag.current = null; }}
              onMouseLeave={() => { drag.current = null; }}
              data-testid="reseau-graph">
              <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom}) translate(${(W * (1 - zoom)) / (2 * zoom)} ${(H * (1 - zoom)) / (2 * zoom)})`}>
                {data.links.map((l, i) => {
                  const a = positions[l.source], b = positions[l.target];
                  if (!a || !b) return null;
                  return (
                    <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                      stroke={l.lien_direct ? "#818cf8" : l.in_crm ? "#cbd5e1" : "#e2e8f0"}
                      strokeWidth={l.lien_direct ? 2.2 : 1.6}
                      strokeDasharray={l.in_crm ? undefined : "4 3"} />
                  );
                })}
                {data.nodes.map((n) => {
                  const p = positions[n.id];
                  if (!p) return null;
                  const r = nodeRadius(n);
                  const active = selected?.id === n.id;
                  const label = (n.label || "").length > 22 ? `${n.label.slice(0, 21)}…` : n.label;
                  return (
                    <g key={n.id} transform={`translate(${p.x} ${p.y})`} className="cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); setSelected(n); }} data-testid="reseau-node">
                      <circle r={r} fill={nodeFill(n)}
                        stroke={active ? "#4f46e5" : "#fff"} strokeWidth={active ? 3 : 2} />
                      <text y={-r - 7} textAnchor="middle" className="fill-slate-600"
                        style={{ fontSize: 11, fontWeight: n.is_root ? 600 : 500, paintOrder: "stroke" }}
                        stroke="#f8fafc" strokeWidth={3.5}>{label}</text>
                    </g>
                  );
                })}
              </g>
            </svg>

            <div className="absolute top-2 right-2 flex flex-col gap-1">
              <button onClick={() => setZoom((z) => Math.min(2.5, z + 0.2))} className="p-1.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"><ZoomIn size={14} /></button>
              <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))} className="p-1.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"><ZoomOut size={14} /></button>
              <button onClick={reset} className="p-1.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-slate-50" title="Recentrer"><LocateFixed size={14} /></button>
            </div>

            <div className="absolute bottom-2 left-3 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
              <Legend color="#334155" label="Cette fiche" />
              <Legend color="#10b981" label="Dirigeant" />
              <Legend color="#2563eb" label="Société au CRM" />
              <Legend color="#94a3b8" label="Hors CRM (registre public)" />
              <Legend color="#818cf8" label="Liaison enregistrée" />
            </div>
          </div>

          {selected && (
            <NodeDetail node={selected} onClose={() => setSelected(null)} navigate={navigate}
              onAdd={addToCrm} adding={adding === selected.id} />
          )}

          <div className="mt-4">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Sociétés liées</h4>
            <div className="space-y-2">
              {data.nodes.filter((n) => n.kind === "company" && !n.is_root).map((n) => (
                <div key={n.id} data-testid="reseau-company-row"
                  className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${n.in_crm ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-400"}`}>
                    <Building2 size={15} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-slate-800 truncate">{n.label}</span>
                      {n.lien_direct && (
                        <span data-testid="reseau-lien-badge" title={n.relation || "Société liée"}
                          className="shrink-0 flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">
                          <LinkIcon size={9} /> Liée
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 truncate">
                      {[n.subtitle, n.ville, n.siren].filter(Boolean).join(" · ")}
                      {n.etat_administratif === "C" && <span className="text-rose-500"> · fermée</span>}
                    </div>
                  </div>
                  {n.in_crm ? (
                    <>
                      {n.status && <StatusBadge status={n.status} />}
                      {n.lead_score != null && <ScoreBadge score={n.lead_score} category={n.score_category} />}
                      <button onClick={() => navigate(`/entreprises/${n.company_id}`)}
                        className="px-2.5 py-1 rounded-lg border border-slate-300 text-xs text-slate-600 hover:bg-white">Ouvrir</button>
                    </>
                  ) : (
                    <button data-testid="reseau-add-company" onClick={() => addToCrm(n)} disabled={adding === n.id}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:opacity-40">
                      {adding === n.id ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Ajouter
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!data.external_used && external && data.dirigeants.length > 0 && (
        <p className="mt-3 text-xs text-amber-600">
          Registre public injoignable — seules les sociétés déjà présentes dans le CRM sont affichées.
        </p>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "slate" }) {
  const cls = { slate: "text-slate-700", indigo: "text-indigo-600", blue: "text-blue-600" }[accent];
  return (
    <div className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white">
      <span className={`font-mono font-semibold text-sm ${cls}`}>{value}</span>
      <span className="text-xs text-slate-400 ml-1.5">{label}</span>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} /> {label}
    </span>
  );
}

function NodeDetail({ node, onClose, navigate, onAdd, adding }) {
  const isPerson = node.kind === "person";
  return (
    <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/50 p-3.5" data-testid="reseau-node-detail">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center text-indigo-600 shrink-0">
          {isPerson ? <UserRound size={17} /> : <Building2 size={17} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-800">{node.label}</div>
          {node.lien_direct && (
            <div className="text-[11px] text-indigo-600 mt-0.5 flex items-center gap-1">
              <LinkIcon size={11} /> {node.relation || "Société liée à cette fiche"}
            </div>
          )}
          <div className="text-xs text-slate-500 mt-0.5">
            {isPerson
              ? [node.subtitle, node.annee_naissance && `né(e) en ${node.annee_naissance}`,
                 node.type_dirigeant === "morale" && "personne morale"].filter(Boolean).join(" · ")
              : [node.subtitle, node.ville, node.siren && `SIREN ${node.siren}`,
                 node.forme_juridique, node.tranche_effectif].filter(Boolean).join(" · ")}
          </div>
          {!isPerson && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {node.in_crm ? (
                <>
                  {node.status && <StatusBadge status={node.status} />}
                  {!node.is_root && (
                    <button onClick={() => navigate(`/entreprises/${node.company_id}`)}
                      className="px-2.5 py-1 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700">Ouvrir la fiche</button>
                  )}
                </>
              ) : (
                <button onClick={() => onAdd(node)} disabled={adding}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:opacity-40">
                  {adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Ajouter au CRM
                </button>
              )}
              {node.siren && (
                <a href={`https://annuaire-entreprises.data.gouv.fr/entreprise/${node.siren}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-300 bg-white text-xs text-slate-600 hover:bg-slate-50">
                  <ExternalLink size={12} /> Annuaire
                </a>
              )}
            </div>
          )}
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
      </div>
    </div>
  );
}
